import React, { useRef, useState } from "react";
import {
  Box,
  Button,
  HStack,
  Heading,
  Image,
  Modal,
  Pressable,
  ScrollView,
  Text,
  VStack,
} from "native-base";
import useDialogA11y from "../../hooks/useDialogA11y";
import { DishAccessibilityInfo, allergenName } from "./DishAccessibilityInfo";

/**
 * DishModal – lets the user pick a size, modifiers, and quantity before
 * adding a dish to the cart.
 *
 * Props
 *   dish        — full DishDetails document from Firestore
 *   onClose     — () => void
 *   onAddToCart — (cartItem) => void
 *     cartItem: { dish, selectedSize, selectedOptions, quantity, lineTotal }
 *   readOnly    — show sizes, options and prices only (no selection, no cart)
 *
 * Option items' `enabled` flag is the app's "selected" state, not availability:
 * the Square import writes enabled:false on every choice. All items are shown.
 *
 * Selection rules come from the menu data (G2):
 *   group.toggle        → single choice (radio); the Square import sets it when
 *                         maxSelections === 1. Otherwise several can be chosen.
 *   group.allowQuantity → each chosen option has a quantity; price × quantity.
 * Price = size price + Σ option price × quantity, all × dish quantity.
 * (The app currently replaces the base price with any priced option; that's a
 * bug in the app and is deliberately not copied.)
 */

// Option groups/items don't always carry ids (legacy docs); fall back to position.
export const groupKey = (group, index) => (group?.id != null && group.id !== "" ? String(group.id) : `g${index}`);
export const itemKey = (item, index) => (item?.id != null && item.id !== "" ? String(item.id) : `i${index}`);

export function DishModal({ dish, onClose, onAddToCart, readOnly = false }) {
  const dialogRef = useRef(null);
  useDialogA11y(dialogRef, onClose);

  const sizes = dish.dishAvailSize || [];
  // Every group that has choices, keyed so selections never collide.
  const optionGroups = (dish.dishOptions || [])
    .map((g, gi) => ({
      ...g,
      _key: groupKey(g, gi),
      _items: (g.optionItems || []).map((item, ii) => ({ ...item, _key: itemKey(item, ii) })),
    }))
    .filter((g) => g._items.length > 0);

  const [selectedSize, setSelectedSize] = useState(sizes[0] || null);
  const [selectedOptions, setSelectedOptions] = useState({});
  // { [groupKey]: [itemKey, ...] }
  const [optionQty, setOptionQty] = useState({});
  // { [groupKey]: { [itemKey]: n } } — only for allowQuantity groups
  const [quantity, setQuantity] = useState(1);

  const dishImage = dish.DishImage || dish.dishPic || dish.dishImage || null;

  // Toggle an option item selection
  const toggleOption = (group, item) => {
    setSelectedOptions((prev) => {
      const gid = group._key;
      const current = prev[gid] || [];
      const alreadySelected = current.includes(item._key);
      if (alreadySelected) {
        // A required single-choice group keeps its choice (like a radio group).
        if (group.toggle && group.required) return prev;
        return { ...prev, [gid]: current.filter((id) => id !== item._key) };
      }
      return { ...prev, [gid]: group.toggle ? [item._key] : [...current, item._key] };
    });
  };

  const qtyOf = (group, item) =>
    group.allowQuantity ? Math.max(1, optionQty[group._key]?.[item._key] || 1) : 1;

  const changeQty = (group, item, delta) =>
    setOptionQty((prev) => {
      const g = prev[group._key] || {};
      const next = Math.min(20, Math.max(1, (g[item._key] || 1) + delta));
      return { ...prev, [group._key]: { ...g, [item._key]: next } };
    });

  const isOptionSelected = (groupKeyValue, itemKeyValue) =>
    (selectedOptions[groupKeyValue] || []).includes(itemKeyValue);

  // Check all required groups have at least one selection
  const requiredGroupsMet = optionGroups
    .filter((g) => g.required)
    .every((g) => (selectedOptions[g._key] || []).length > 0);

  // Calculate line total
  const basePrice = parseFloat(selectedSize?.price || 0);
  const optionsTotal = optionGroups.reduce((sum, group) => {
    const selected = selectedOptions[group._key] || [];
    return (
      sum +
      group._items
        .filter((item) => selected.includes(item._key))
        .reduce((s, item) => s + parseFloat(item.price || 0) * qtyOf(group, item), 0)
    );
  }, 0);
  const lineTotal = (basePrice + optionsTotal) * quantity;

  const handleAdd = () => {
    // Cart consumers (CartSheet) match selections against optionItems[].id.
    const selectedOptionIds = {};
    const selectedOptionQuantities = {};
    optionGroups.forEach((g) => {
      const chosen = selectedOptions[g._key] || [];
      if (chosen.length) {
        const items = g._items.filter((i) => chosen.includes(i._key));
        selectedOptionIds[g.id ?? g._key] = items.map((i) => i.id ?? i._key);
        if (g.allowQuantity) {
          selectedOptionQuantities[g.id ?? g._key] = Object.fromEntries(
            items.map((i) => [i.id ?? i._key, qtyOf(g, i)])
          );
        }
      }
    });
    onAddToCart({
      dish,
      selectedSize,
      selectedOptions: selectedOptionIds,
      optionQuantities: selectedOptionQuantities,
      quantity,
      lineTotal,
    });
    onClose();
  };

  return (
    <div ref={dialogRef} role="dialog" aria-modal="true" aria-label={readOnly ? `${dish.dishName}: options and prices` : `Customise ${dish.dishName}`}>
    <Modal isOpen onClose={onClose} size="xl" avoidKeyboard>
      <Modal.Content maxH="90%" borderRadius="2xl">
        <Modal.CloseButton accessibilityLabel="Close" />

        {/* Hero image */}
        {dishImage && (
          <Box w="100%" h="180px" overflow="hidden" borderTopRadius="2xl">
            <Image
              source={{ uri: dishImage }}
              alt={dish.dishName}
              w="100%"
              h="100%"
              resizeMode="cover"
            />
          </Box>
        )}

        <ScrollView>
          <Box px={5} py={4}>
            {/* Name + price */}
            <HStack justifyContent="space-between" alignItems="flex-start" mb={1}>
              <Heading size="md" flex={1} mr={3}>
                {dish.dishName}
              </Heading>
              {selectedSize && !(readOnly && sizes.length > 1) && (
                <Text fontWeight="bold" color="red.500" fontSize="md">
                  ${parseFloat(selectedSize.price).toFixed(2)}
                </Text>
              )}
            </HStack>

            {dish.dishDesc ? (
              <Text color="coolGray.600" fontSize="sm" mb={4}>
                {dish.dishDesc}
              </Text>
            ) : null}

            <DishAccessibilityInfo accessibility={dish.accessibility} />

            {/* Sizes (read-only list) */}
            {readOnly && sizes.length > 1 && (
              <VStack space={1} mb={4}>
                <Text fontWeight="semibold" color="coolGray.700">
                  Sizes
                </Text>
                {sizes.map((size, si) => (
                  <HStack key={size.id ?? si} justifyContent="space-between">
                    <Text fontSize="sm" color="coolGray.800">{size.size}</Text>
                    <Text fontSize="sm" color="coolGray.600">${parseFloat(size.price).toFixed(2)}</Text>
                  </HStack>
                ))}
              </VStack>
            )}

            {/* Size picker */}
            {!readOnly && sizes.length > 1 && (
              <VStack space={2} mb={4}>
                <Text fontWeight="semibold" color="coolGray.700">
                  Size
                </Text>
                <HStack space={2} flexWrap="wrap">
                  {sizes.map((size) => {
                    const isSelected = selectedSize?.id === size.id;
                    return (
                      <Pressable
                        key={size.id}
                        onPress={() => setSelectedSize(size)}
                        accessibilityRole="radio"
                        accessibilityState={{ checked: isSelected }}
                        accessibilityLabel={`${size.size}: $${parseFloat(
                          size.price
                        ).toFixed(2)}`}
                      >
                        <Box
                          borderWidth={2}
                          borderColor={isSelected ? "red.500" : "coolGray.200"}
                          borderRadius="md"
                          px={3}
                          py={2}
                          bg={isSelected ? "red.50" : "white"}
                        >
                          <Text
                            fontWeight={isSelected ? "bold" : "normal"}
                            color={isSelected ? "red.600" : "coolGray.700"}
                            fontSize="sm"
                          >
                            {size.size}
                          </Text>
                          <Text fontSize="xs" color="coolGray.600">
                            ${parseFloat(size.price).toFixed(2)}
                          </Text>
                        </Box>
                      </Pressable>
                    );
                  })}
                </HStack>
              </VStack>
            )}

            {/* Option groups (read-only list) */}
            {readOnly &&
              optionGroups.map((group) => (
                <VStack key={group._key} space={1} mb={4}>
                  <HStack space={1} alignItems="center">
                    <Text fontWeight="semibold" color="coolGray.700">
                      {group.title}
                    </Text>
                    {group.required && (
                      <Text fontSize="xs" color="red.500">
                        (required)
                      </Text>
                    )}
                  </HStack>
                  {group._items.map((item) => (
                    <HStack key={item._key} justifyContent="space-between">
                      <Text fontSize="sm" color="coolGray.800">
                        {item.name}
                        {Array.isArray(item.allergens) && item.allergens.length > 0
                          ? ` (contains ${item.allergens.map(allergenName).join(", ")})`
                          : ""}
                      </Text>
                      {parseFloat(item.price) ? (
                        <Text fontSize="sm" color="coolGray.600">
                          +${parseFloat(item.price).toFixed(2)}
                        </Text>
                      ) : null}
                    </HStack>
                  ))}
                </VStack>
              ))}

            {/* Option groups */}
            {!readOnly &&
              optionGroups.map((group) => (
                <VStack key={group._key} space={2} mb={4}>
                  <HStack space={1} alignItems="center">
                    <Text fontWeight="semibold" color="coolGray.700">
                      {group.title}
                    </Text>
                    {group.required && (
                      <Text fontSize="xs" color="red.500">
                        (required)
                      </Text>
                    )}
                    {group.toggle && (
                      <Text fontSize="xs" color="coolGray.600">
                        Choose one
                      </Text>
                    )}
                  </HStack>
                  {group._items
                    .map((item) => {
                      const selected = isOptionSelected(group._key, item._key);
                      const qty = qtyOf(group, item);
                      return (
                        <React.Fragment key={item._key}>
                        <Pressable
                          onPress={() => toggleOption(group, item)}
                          accessibilityRole={group.toggle ? "radio" : "checkbox"}
                          accessibilityState={{ checked: selected }}
                          accessibilityLabel={`${item.name}${
                            item.price ? ` +$${parseFloat(item.price).toFixed(2)}` : ""
                          }`}
                        >
                          <HStack
                            justifyContent="space-between"
                            alignItems="center"
                            px={3}
                            py={2}
                            borderRadius="md"
                            borderWidth={1}
                            borderColor={selected ? "red.400" : "coolGray.200"}
                            bg={selected ? "red.50" : "white"}
                          >
                            <HStack space={2} alignItems="center">
                              <Box
                                w={5}
                                h={5}
                                borderRadius={group.toggle ? "full" : "sm"}
                                borderWidth={2}
                                borderColor={selected ? "red.500" : "coolGray.300"}
                                bg={selected ? "red.500" : "white"}
                                justifyContent="center"
                                alignItems="center"
                              >
                                {selected && (
                                  <Text color="white" fontSize="xs" lineHeight={14}>
                                    ✓
                                  </Text>
                                )}
                              </Box>
                              <Text fontSize="sm" color="coolGray.800">
                                {item.name}
                              </Text>
                            </HStack>
                            {item.price ? (
                              <Text fontSize="sm" color="coolGray.600">
                                +${parseFloat(item.price).toFixed(2)}
                              </Text>
                            ) : null}
                          </HStack>
                        </Pressable>
                        {group.allowQuantity && selected && (
                          <HStack space={3} alignItems="center" ml={8} mb={1}>
                            <Pressable
                              onPress={() => changeQty(group, item, -1)}
                              accessibilityLabel={`Fewer ${item.name}`}
                              disabled={qty <= 1}
                            >
                              <Text fontWeight="bold" fontSize="lg" color="coolGray.700">−</Text>
                            </Pressable>
                            <Text fontSize="sm" accessibilityLabel={`${item.name} quantity: ${qty}`}>
                              {qty}
                            </Text>
                            <Pressable
                              onPress={() => changeQty(group, item, 1)}
                              accessibilityLabel={`More ${item.name}`}
                            >
                              <Text fontWeight="bold" fontSize="lg" color="coolGray.700">+</Text>
                            </Pressable>
                          </HStack>
                        )}
                        </React.Fragment>
                      );
                    })}
                </VStack>
              ))}

            {/* Quantity */}
            {!readOnly && (
            <VStack space={2} mb={6}>
              <Text fontWeight="semibold" color="coolGray.700">
                Quantity
              </Text>
              <HStack alignItems="center" space={4}>
                <Pressable
                  onPress={() => setQuantity((q) => Math.max(1, q - 1))}
                  accessibilityLabel="Decrease quantity"
                  disabled={quantity <= 1}
                >
                  <Box
                    w={9}
                    h={9}
                    borderRadius="full"
                    bg={quantity <= 1 ? "coolGray.100" : "coolGray.200"}
                    justifyContent="center"
                    alignItems="center"
                  >
                    <Text fontWeight="bold" fontSize="xl" color="coolGray.700">
                      −
                    </Text>
                  </Box>
                </Pressable>
                <Text
                  fontWeight="bold"
                  fontSize="lg"
                  minW="24px"
                  textAlign="center"
                  accessibilityLabel={`Quantity: ${quantity}`}
                >
                  {quantity}
                </Text>
                <Pressable
                  onPress={() => setQuantity((q) => Math.min(20, q + 1))}
                  accessibilityLabel="Increase quantity"
                >
                  <Box
                    w={9}
                    h={9}
                    borderRadius="full"
                    bg="coolGray.200"
                    justifyContent="center"
                    alignItems="center"
                  >
                    <Text fontWeight="bold" fontSize="xl" color="coolGray.700">
                      +
                    </Text>
                  </Box>
                </Pressable>
              </HStack>
            </VStack>
            )}
          </Box>
        </ScrollView>

        {/* Footer: Add to cart */}
        {!readOnly && (
        <Box px={5} pb={5} pt={2} borderTopWidth={1} borderTopColor="coolGray.100">
          <Button
            bg={requiredGroupsMet && selectedSize ? "red.600" : "coolGray.300"}
            _text={{ color: "white", fontWeight: "bold", fontSize: "md" }}
            onPress={handleAdd}
            isDisabled={!requiredGroupsMet || !selectedSize}
            accessibilityLabel={`Add ${quantity} ${dish.dishName} to cart — $${lineTotal.toFixed(
              2
            )}`}
          >
            Add to cart · ${lineTotal.toFixed(2)}
          </Button>
        </Box>
        )}
      </Modal.Content>
    </Modal>
    </div>
  );
}
